import { useRef, useState } from "react";
import { api, adminAuth, imageUrl } from "../../lib/api";
import { toast } from "sonner";
import { Star, Upload, AlertTriangle, X, Trash2, GripVertical, Link2 } from "lucide-react";
import ColorsSizesEditor from "./ColorsSizesEditor";

export default function ProductsTab({ products, setProducts }) {
  const [uploadingId, setUploadingId] = useState(null);
  const dragPreset = useRef(null); // { productId, index }
  const [dragOverPreset, setDragOverPreset] = useState(null);
  const dragGallery = useRef(null);
  const [dragOverGallery, setDragOverGallery] = useState(null);
  const dragProduct = useRef(null); // index
  const [dragOverProduct, setDragOverProduct] = useState(null);

  const reorderProducts = (from, to) => {
    if (from === to || from == null || to == null) return;
    let newList = null;
    setProducts((prev) => {
      const list = [...prev];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      newList = list;
      return list;
    });
    if (newList) {
      api
        .patch(`/admin/products/reorder`, { order: newList.map((p) => p.id) }, { headers: adminAuth() })
        .then(() => toast.success("Product order saved"))
        .catch(() => { toast.error("Reorder failed"); });
    }
  };

  const reorderPresets = (productId, from, to) => {
    if (from === to || from == null || to == null) return;
    let newList = null;
    setProducts((prev) => {
      const acting = prev.find((p) => p.id === productId);
      const list = [...(acting?.preset_designs || [])];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      newList = list;
      const gid = acting?.preset_group_id;
      return prev.map((p) =>
        p.id === productId || (gid && p.preset_group_id === gid) ? { ...p, preset_designs: list } : p
      );
    });
    if (newList) {
      api
        .patch(`/admin/products/${productId}/presets/reorder`, { order: newList.map((d) => d.id) }, { headers: adminAuth() })
        .then(() => toast.success("Order saved"))
        .catch(() => { toast.error("Reorder failed"); });
    }
  };

  const reorderGallery = (productId, from, to) => {
    if (from === to || from == null || to == null) return;
    let newList = null;
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id !== productId) return p;
        const list = [...(p.gallery_images || [])];
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        newList = list;
        return { ...p, gallery_images: list };
      })
    );
    if (newList) {
      api
        .patch(`/admin/products/${productId}/gallery/reorder`, { order: newList.map((d) => d.id) }, { headers: adminAuth() })
        .then(() => toast.success("Order saved"))
        .catch(() => { toast.error("Reorder failed"); });
    }
  };

  const uploadImage = async (productId, file) => {
    if (!file) return;
    setUploadingId(productId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/admin/products/${productId}/image`, fd, {
        headers: { ...adminAuth(), "Content-Type": "multipart/form-data" },
      });
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, mockup_image: res.data.mockup_image } : p)));
      toast.success("Product photo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="card-rustic">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/PRODUCTS</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">Product Photos</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Upload PNG, JPG or WEBP up to 15MB. Drag the <span className="inline-flex align-middle"><GripVertical className="w-3 h-3"/></span> handle to reorder how products appear in the shop.</div>
      </div>
      <div className="divide-y divide-[rgba(27,42,65,0.06)]">
        {products.map((p, pIdx) => (
          <div
            key={p.id}
            onDragEnter={() => { if (dragProduct.current != null) setDragOverProduct(pIdx); }}
            onDragOver={(e) => { if (dragProduct.current != null) e.preventDefault(); }}
            onDrop={(e) => {
              if (dragProduct.current != null) {
                e.preventDefault();
                reorderProducts(dragProduct.current, pIdx);
              }
              dragProduct.current = null;
              setDragOverProduct(null);
            }}
            className={`p-5 flex flex-col sm:flex-row gap-5 items-start transition ${dragOverProduct === pIdx ? "bg-[var(--gold)]/10 ring-2 ring-[var(--gold)] ring-inset" : ""}`}
            data-testid={`admin-product-${p.id}`}
          >
            <button
              type="button"
              draggable
              onDragStart={() => { dragProduct.current = pIdx; }}
              onDragEnd={() => { dragProduct.current = null; setDragOverProduct(null); }}
              className="self-center text-[var(--ink-soft)] hover:text-[var(--navy)] cursor-grab active:cursor-grabbing p-1 -ml-1"
              aria-label={`Drag to reorder ${p.name}`}
              data-testid={`admin-product-drag-${p.id}`}
            >
              <GripVertical className="w-5 h-5"/>
            </button>
            <div className="w-32 h-32 rounded-sm overflow-hidden bg-[var(--bone-2)] border border-[rgba(27,42,65,0.1)] flex-shrink-0">
              <img src={imageUrl(p.mockup_image)} alt={p.name} className="w-full h-full object-cover"/>
            </div>
            <div className="flex-1">
              <div className="font-serif-display text-xl text-[var(--navy)]">{p.name}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mt-1">{p.category} · ${p.base_price?.toFixed(2)} · {p.is_active ? "active" : "hidden"}</div>
              {p.stock_count <= (p.low_stock_threshold ?? 5) && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-[var(--crimson)]/10 text-[var(--crimson)] text-[10px] font-mono-ui tracking-[0.2em] uppercase" data-testid={`low-stock-${p.id}`}>
                  <AlertTriangle className="w-3 h-3"/> LOW STOCK · {p.stock_count} LEFT
                </div>
              )}
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer btn-ghost py-2 px-3 text-xs">
                <Upload className="w-3.5 h-3.5"/>
                {uploadingId === p.id ? "Uploading…" : "Upload Photo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={uploadingId === p.id}
                  onChange={(e) => uploadImage(p.id, e.target.files?.[0])}
                  data-testid={`admin-upload-${p.id}`}
                />
              </label>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">Price $</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={p.base_price}
                    className="input-field w-24 text-sm"
                    data-testid={`admin-price-${p.id}`}
                    onBlur={async (e) => {
                      const v = parseFloat(e.target.value);
                      if (isNaN(v) || v === p.base_price) return;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { base_price: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, base_price: v } : x));
                        toast.success("Price updated");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">Ship $</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={p.shipping_fee ?? ""}
                    placeholder="default"
                    className="input-field w-24 text-sm"
                    data-testid={`admin-shipping-${p.id}`}
                    onBlur={async (e) => {
                      const raw = e.target.value.trim();
                      const v = raw === "" ? null : parseFloat(raw);
                      if (raw !== "" && (isNaN(v) || v < 0)) { toast.error("Invalid shipping fee"); return; }
                      if ((p.shipping_fee ?? null) === v) return;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { shipping_fee: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, shipping_fee: v } : x));
                        toast.success("Shipping updated");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`admin-customizable-label-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={p.customizable !== false}
                    className="w-4 h-4 accent-[var(--crimson)] cursor-pointer"
                    data-testid={`admin-customizable-${p.id}`}
                    onChange={async (e) => {
                      const v = e.target.checked;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { customizable: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, customizable: v } : x));
                        toast.success(v ? "Customizable" : "Sold as-is");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">Customizable</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`admin-active-label-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={p.is_active !== false}
                    className="w-4 h-4 accent-[var(--navy)] cursor-pointer"
                    data-testid={`admin-active-${p.id}`}
                    onChange={async (e) => {
                      const v = e.target.checked;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { is_active: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, is_active: v } : x));
                        toast.success(v ? "Visible in shop" : "Hidden from shop");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">Visible</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`admin-featured-label-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={!!p.featured}
                    className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
                    data-testid={`admin-featured-${p.id}`}
                    onChange={async (e) => {
                      const v = e.target.checked;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { featured: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: v } : x));
                        toast.success(v ? "Marked as featured" : "Removed featured");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                  <span className="inline-flex items-center gap-1 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]"><Star className={`w-3 h-3 ${p.featured ? "fill-[var(--gold)] text-[var(--gold)]" : ""}`}/> Featured</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`admin-exclude-discounts-label-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={!!p.exclude_from_discounts}
                    className="w-4 h-4 accent-[var(--crimson)] cursor-pointer"
                    data-testid={`admin-exclude-discounts-${p.id}`}
                    onChange={async (e) => {
                      const v = e.target.checked;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { exclude_from_discounts: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, exclude_from_discounts: v } : x));
                        toast.success(v ? "No discounts on this item" : "Discounts allowed");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">No Discounts</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`admin-exclude-shipping-label-${p.id}`}>
                  <input
                    type="checkbox"
                    checked={!!p.exclude_from_free_shipping}
                    className="w-4 h-4 accent-[var(--crimson)] cursor-pointer"
                    data-testid={`admin-exclude-shipping-${p.id}`}
                    onChange={async (e) => {
                      const v = e.target.checked;
                      try {
                        await api.patch(`/admin/products/${p.id}`, { exclude_from_free_shipping: v }, { headers: adminAuth() });
                        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, exclude_from_free_shipping: v } : x));
                        toast.success(v ? "Always charges shipping" : "Eligible for free shipping");
                      } catch { toast.error("Update failed"); }
                    }}
                  />
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">No Free Ship</span>
                </label>
              </div>
              <ColorsSizesEditor product={p} onSaved={(patch) => setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, ...patch } : x))} />
              <div className="mt-4 pt-3 border-t border-[rgba(27,42,65,0.06)]">
                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-2 flex flex-wrap items-center gap-2">
                  <span>Preset designs ({p.preset_designs?.length || 0}){(p.preset_designs?.length || 0) > 1 && <span className="ml-2 normal-case tracking-normal text-[9px] text-[var(--ink-soft)]/70">· drag to reorder</span>}</span>
                  {p.preset_group_id && (
                    <span className="inline-flex items-center gap-1 normal-case tracking-normal bg-[var(--crimson)]/10 text-[var(--crimson)] px-2 py-0.5 rounded-full text-[9px]" data-testid={`admin-product-shared-presets-${p.id}`}>
                      <Link2 className="w-3 h-3"/> Shared set — changes sync to linked products
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(p.preset_designs || []).map((d, idx) => (
                    <div
                      key={d.id}
                      draggable
                      onDragStart={() => { dragPreset.current = { productId: p.id, index: idx }; }}
                      onDragEnter={() => { if (dragPreset.current?.productId === p.id) setDragOverPreset(`${p.id}:${idx}`); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = dragPreset.current;
                        if (src && src.productId === p.id) reorderPresets(p.id, src.index, idx);
                        dragPreset.current = null;
                        setDragOverPreset(null);
                      }}
                      onDragEnd={() => { dragPreset.current = null; setDragOverPreset(null); }}
                      className={`relative w-14 h-14 rounded-sm border bg-[var(--bone-2)] overflow-hidden cursor-grab active:cursor-grabbing transition ${dragOverPreset === `${p.id}:${idx}` ? "border-[var(--gold)] ring-2 ring-[var(--gold)]" : "border-[rgba(27,42,65,0.15)]"}`}
                      data-testid={`admin-preset-${d.id}`}
                    >
                      <img src={imageUrl(d.image_url)} alt={d.name} className="w-full h-full object-contain p-1 pointer-events-none"/>
                      <button
                        type="button"
                        aria-label={`Remove ${d.name}`}
                        data-testid={`admin-preset-remove-${d.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Remove "${d.name}"?`)) return;
                          try {
                            await api.delete(`/admin/products/${p.id}/presets/${d.id}`, { headers: adminAuth() });
                            setProducts((prev) => prev.map((x) => (x.id === p.id || (p.preset_group_id && x.preset_group_id === p.preset_group_id)) ? { ...x, preset_designs: (x.preset_designs || []).filter((pd) => pd.id !== d.id) } : x));
                            toast.success("Removed");
                          } catch { toast.error("Remove failed"); }
                        }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--crimson)] text-white grid place-items-center shadow-md hover:scale-110 transition z-10"
                      >
                        <X className="w-3 h-3" strokeWidth={3}/>
                      </button>
                    </div>
                  ))}
                  <label className="w-14 h-14 rounded-sm border-2 border-dashed border-[rgba(27,42,65,0.2)] hover:border-[var(--gold)] grid place-items-center cursor-pointer text-[var(--ink-soft)] hover:text-[var(--navy)]" data-testid={`admin-preset-add-${p.id}`}>
                    <Upload className="w-4 h-4"/>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const name = window.prompt("Design name:", f.name.replace(/\.[^.]+$/, "")) || f.name;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const r = await api.post(`/admin/products/${p.id}/presets?name=${encodeURIComponent(name)}`, fd, { headers: { ...adminAuth(), "Content-Type": "multipart/form-data" } });
                          setProducts((prev) => prev.map((x) => (x.id === p.id || (p.preset_group_id && x.preset_group_id === p.preset_group_id)) ? { ...x, preset_designs: [...(x.preset_designs || []), r.data] } : x));
                          toast.success("Preset added");
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-[rgba(27,42,65,0.06)]">
                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-2">Gallery photos ({p.gallery_images?.length || 0}/6){(p.gallery_images?.length || 0) > 1 && <span className="ml-2 normal-case tracking-normal text-[9px] text-[var(--ink-soft)]/70">· drag to reorder</span>}</div>
                <div className="flex flex-wrap gap-2">
                  {(p.gallery_images || []).map((g, idx) => (
                    <div
                      key={g.id}
                      draggable
                      onDragStart={() => { dragGallery.current = { productId: p.id, index: idx }; }}
                      onDragEnter={() => { if (dragGallery.current?.productId === p.id) setDragOverGallery(`${p.id}:${idx}`); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = dragGallery.current;
                        if (src && src.productId === p.id) reorderGallery(p.id, src.index, idx);
                        dragGallery.current = null;
                        setDragOverGallery(null);
                      }}
                      onDragEnd={() => { dragGallery.current = null; setDragOverGallery(null); }}
                      className={`relative w-14 h-14 rounded-sm border bg-[var(--bone-2)] overflow-hidden cursor-grab active:cursor-grabbing transition ${dragOverGallery === `${p.id}:${idx}` ? "border-[var(--gold)] ring-2 ring-[var(--gold)]" : "border-[rgba(27,42,65,0.15)]"}`}
                      data-testid={`admin-gallery-${g.id}`}
                    >
                      <img src={imageUrl(g.image_url)} alt="" className="w-full h-full object-cover pointer-events-none"/>
                      <button
                        type="button"
                        aria-label="Remove photo"
                        data-testid={`admin-gallery-remove-${g.id}`}
                        onClick={async () => {
                          if (!window.confirm("Remove this photo?")) return;
                          try {
                            await api.delete(`/admin/products/${p.id}/gallery/${g.id}`, { headers: adminAuth() });
                            setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, gallery_images: x.gallery_images.filter((gi) => gi.id !== g.id) } : x));
                            toast.success("Removed");
                          } catch { toast.error("Remove failed"); }
                        }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--crimson)] text-white grid place-items-center shadow-md hover:scale-110 transition z-10"
                      >
                        <X className="w-3 h-3" strokeWidth={3}/>
                      </button>
                    </div>
                  ))}
                  {(p.gallery_images?.length || 0) < 6 && (
                  <label className="w-14 h-14 rounded-sm border-2 border-dashed border-[rgba(27,42,65,0.2)] hover:border-[var(--gold)] grid place-items-center cursor-pointer text-[var(--ink-soft)] hover:text-[var(--navy)]" data-testid={`admin-gallery-add-${p.id}`}>
                    <Upload className="w-4 h-4"/>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const r = await api.post(`/admin/products/${p.id}/gallery`, fd, { headers: { ...adminAuth(), "Content-Type": "multipart/form-data" } });
                          setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, gallery_images: [...(x.gallery_images || []), r.data] } : x));
                          toast.success("Photo added");
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                        e.target.value = "";
                      }}
                    />
                  </label>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-[rgba(27,42,65,0.06)]">
                <button
                  type="button"
                  data-testid={`admin-product-delete-${p.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Permanently delete "${p.name}"? This can't be undone. (Past orders keep their own records.)`)) return;
                    try {
                      await api.delete(`/admin/products/${p.id}`, { headers: adminAuth() });
                      setProducts((prev) => prev.filter((x) => x.id !== p.id));
                      toast.success(`"${p.name}" deleted`);
                    } catch { toast.error("Delete failed"); }
                  }}
                  className="inline-flex items-center gap-2 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-3 py-2 rounded-sm font-mono-ui text-[10px] tracking-[0.25em] uppercase transition"
                >
                  <Trash2 className="w-3.5 h-3.5"/> Delete Product
                </button>
              </div>
            </div>
          </div>
        ))}
        {products.length === 0 && (
          <div className="p-10 text-center text-[var(--ink-soft)]">No products yet.</div>
        )}
      </div>
    </div>
  );
}
